import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Circle, CircleCheck, Undo2, X } from "lucide-react";
import confetti from "canvas-confetti";
import { AnimatePresence, MotionConfig, motion } from "motion/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "wouter";

import { CanvasPivotPanel } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import { PanelToolbar } from "@/components/layout/SplitPanel/PanelToolbar.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import {
  cardListClass,
  compactRowClass,
  threadStatusConfig,
} from "@/components/threads/shared.tsx";
import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import { DISMISS_LABEL, DISMISS_REASON_LABEL, KEEP_TASK } from "@/shared/taskRowCopy.ts";
import { dueShort } from "@/shared/userTaskDue.ts";
import {
  SIMPLE_CATEGORY_LABEL,
  SIMPLE_EMPTY_TEXT,
  SIMPLE_GROUP_HEADING,
  snoozedBackLabel,
  taskThreadMark,
  taskThreadStatusLabel,
} from "@/shared/userTaskSimpleList.ts";
import { userTaskHref } from "@/types/userTaskLinks.ts";
import {
  dismissReasons,
  type SimpleTaskCategory,
  type SimpleTaskGroup,
  simpleTaskCategories,
  type UserTask,
} from "@/types/userTasks.ts";
import type { PanelControlAxes, PanelControlDimension } from "@/views/Settings/panelControls.tsx";
import {
  shownStatus,
  useAnswerTriage,
  userTaskQueries,
  useSetUserTaskStatus,
  useUndoDismissal,
} from "./api.ts";
import { StartTaskDialog } from "./StartTaskDialog.tsx";
import {
  DismissedStrip,
  DismissedStripsProvider,
  useDismissedStrips,
  withStrips,
} from "./DismissedStrip.tsx";

/**
 * The simpler Tasks page (`tasks-simple-list`): Open, Snoozed and Closed, each
 * row a title with a few fixed actions. See `docs/prds/tasks-simple-list.md`.
 */
export const SimpleTasksView = ({
  accountSwitcher,
  leading,
}: {
  accountSwitcher?: ReactNode;
  leading?: ReactNode;
}) => {
  const [category, setCategory] = useState<SimpleTaskCategory>("open");
  const { data, isLoading, isPlaceholderData, isError, refetch } = useQuery({
    ...userTaskQueries.simple(category),
    // Switching category keeps the last one on screen until the next arrives,
    // rather than flashing the loader.
    placeholderData: keepPreviousData,
  });
  const groups = data?.groups ?? [];

  const categoryAxis: PanelControlDimension = {
    label: "Category",
    value: category,
    onChange: (next) => {
      const picked = simpleTaskCategories.find((c) => c === next);
      if (picked) setCategory(picked);
    },
    options: simpleTaskCategories.map((c) => ({
      value: c,
      label: SIMPLE_CATEGORY_LABEL[c],
    })),
    defaultValue: "open",
    display: "segmented",
    statsigName: "tasks-simple-category",
  };
  const controls: PanelControlAxes = { filters: [categoryAxis] };

  let body: ReactNode;
  // A category not loaded yet shows the loader, not the last category's tasks
  // filtered down to nothing, which would read as an empty category.
  if (isLoading || isPlaceholderData) {
    body = <Loader className="py-8" />;
  } else if (isError) {
    body = (
      <div className="flex flex-col items-start gap-2 p-4 text-sm text-muted-foreground">
        Couldn't load your tasks.
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          data-statsig-name="tasks-simple-retry"
        >
          Try again
        </Button>
      </div>
    );
  } else {
    // Keyed by category: strips belong to the list they were left in, and go
    // when the user moves to another.
    body = (
      <MotionConfig reducedMotion="user">
        <DismissedStripsProvider key={category}>
          <TaskGroups groups={groups} category={category} />
        </DismissedStripsProvider>
      </MotionConfig>
    );
  }

  return (
    <CanvasPivotPanel
      title="Tasks"
      leading={leading}
      accountSwitcher={accountSwitcher}
      toolbar={
        <PanelToolbar controls={controls} subject="tasks" statsigName="tasks-simple-toolbar" />
      }
    >
      {body}
    </CanvasPivotPanel>
  );
};

/** The groups each category shows, top to bottom. */
const GROUP_ORDER: Record<SimpleTaskCategory, SimpleTaskGroup["key"][]> = {
  open: ["suggested", "open"],
  snoozed: ["snoozed"],
  closed: ["closed"],
};

/** The category's groups, with each just-dismissed task's strip where it sat. */
export const TaskGroups = ({
  groups,
  category,
}: {
  groups: SimpleTaskGroup[];
  category: SimpleTaskCategory;
}) => {
  const { entries } = useDismissedStrips();
  // While strips show, the rows keep the places they had when the first one
  // went up, so a strip stays among the rows it sat with even if the server
  // reorders them meanwhile (another task started, say). Recorded during
  // render, and let go once the last strip leaves.
  const [frozen, setFrozen] = useState<ReadonlyMap<string, number> | null>(null);
  if (entries.length > 0 && !frozen) {
    setFrozen(new Map(groups.flatMap((g) => g.tasks.map((t, i) => [t.id, i] as const))));
  } else if (entries.length === 0 && frozen) {
    setFrozen(null);
  }
  // Every group stays mounted, empty or not: the last row leaving a group
  // needs the group's AnimatePresence there to play its exit.
  const shown = withStrips(groups, entries, GROUP_ORDER[category], frozen ?? undefined);
  const filled = shown.filter((group) => group.rows.length > 0);
  // A heading only earns its place when Open holds two groups; one group is
  // already named by the category picked above it.
  const showHeadings = filled.length > 1;
  return (
    // `overflow-x-hidden`: a done row slides off to the right, and should leave
    // without a sideways scrollbar flashing in.
    <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto pb-4">
      {filled.length === 0 && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {SIMPLE_EMPTY_TEXT[category]}
        </p>
      )}
      {shown.map((group) => (
        <section
          key={group.key}
          // The space between groups belongs to a group with rows, so an empty
          // one takes none.
          className={cn("flex flex-col gap-2", group.rows.length > 0 && "mb-5")}
          data-task-group={group.key}
        >
          {showHeadings && group.rows.length > 0 && (
            <h2 className="text-xs font-medium text-muted-foreground">
              {SIMPLE_GROUP_HEADING[group.key]}
            </h2>
          )}
          {/* `relative`: a row on its way out leaves the flow and is placed
              against this, so the rows below close up by sliding. */}
          <ul className={cn(cardListClass, "relative")}>
            <AnimatePresence initial={false}>
              {group.rows.map((row, index) =>
                // A key of its own, not the task's: under the same key the list
                // took the strip for the row it already had and skipped the
                // strip's opening animations. The row leaves out of the flow
                // as the strip arrives, so nothing below moves.
                "strip" in row ? (
                  <DismissedStrip key={`strip-${row.strip.task.id}`} entry={row.strip} />
                ) : (
                  <SimpleTaskRow
                    key={row.task.id}
                    task={row.task}
                    groupKey={group.key}
                    index={index}
                  />
                ),
              )}
            </AnimatePresence>
          </ul>
        </section>
      ))}
    </div>
  );
};

/** One task: a circle to finish it, its title, its due date, and its actions. */
export const SimpleTaskRow = ({
  task,
  groupKey,
  index,
}: {
  task: UserTask;
  /** Where the row sits, so a dismissal's strip takes its place. */
  groupKey: SimpleTaskGroup["key"];
  index: number;
}) => {
  const { add, remove, settle } = useDismissedStrips();
  const [starting, setStarting] = useState(false);
  // Checked the moment it is tapped, so the check and the strike land before
  // the list drops the row; back if the write fails.
  const [doneNow, setDoneNow] = useState(false);
  const flash = useThreadArrival(task.threadId);
  const triage = useAnswerTriage();
  const setStatus = useSetUserTaskStatus();
  const undo = useUndoDismissal();
  const busy = triage.isPending || setStatus.isPending;
  const taskId = task.id;
  const fromStatus = shownStatus(task.status);
  // Acts on the tap: the strip goes up at once, and comes down again if the
  // dismissal fails, so the row reappears. Awaited rather than given to
  // `mutate`'s callbacks, which do not run once this row has left the list.
  const handleDismiss = () => {
    add({ task, groupKey, index, pending: true });
    const request =
      task.status === "suggested"
        ? triage.mutateAsync({ taskId, answer: "dismiss" })
        : setStatus.mutateAsync({ taskId, fromStatus, status: "cancelled" });
    request.then(
      () => settle(task.id),
      () => remove(task.id, { restored: true }),
    );
  };
  const closed = task.status === "done" || task.status === "cancelled";
  const needsYou =
    !!task.threadId && taskThreadMark(task.threadStatus) === "needsAttention" && !closed;
  const due = closed ? null : dueShort(task, Date.now());
  const back = snoozedBackLabel(task);

  let leadingMark: ReactNode = null;
  if (task.status === "open" || task.status === "later") {
    leadingMark = (
      <CircleButton
        label={`Mark "${task.title}" done`}
        statsigName="tasks-simple-done"
        disabled={busy}
        checked={doneNow}
        pop
        onClick={(event) => {
          setDoneNow(true);
          celebrateDone(event.currentTarget);
          setStatus.mutate(
            { taskId, fromStatus, status: "done" },
            { onError: () => setDoneNow(false) },
          );
        }}
      />
    );
  } else if (task.status === "done") {
    leadingMark = (
      <CircleButton
        checked
        label={`Reopen "${task.title}"`}
        statsigName="tasks-simple-reopen"
        disabled={busy}
        onClick={() => setStatus.mutate({ taskId, fromStatus, status: "open", resumeWork: false })}
      />
    );
  } else if (task.status === "cancelled") {
    leadingMark = (
      <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">
        <X className="size-4" aria-hidden />
      </span>
    );
  }

  let actions: ReactNode = null;
  if (task.status === "suggested") {
    actions = (
      <>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(ROW_ACTION_CLASS, "bg-white")}
          data-statsig-name="tasks-simple-keep"
          disabled={busy}
          onClick={() => triage.mutate({ taskId, answer: "keep" })}
        >
          {KEEP_TASK}
        </Button>
        <DismissIcon disabled={busy} onClick={handleDismiss} />
      </>
    );
  } else if (task.status === "open" || task.status === "later") {
    actions = (
      <>
        <ThreadButton task={task} disabled={busy} onStart={() => setStarting(true)} />
        <DismissIcon disabled={busy} onClick={handleDismiss} />
      </>
    );
  } else if (closed) {
    // Closed rows: View or Answer where there is a thread, in the same column
    // as on Open, and Reopen where Dismiss sits there. A task a Dismiss closed
    // goes back through Undo, which also takes back the answer detection
    // learned from; a done task, or one dropped before Undo existed, reopens
    // as open. A suggestion a mute dismissed has no way back from here: it
    // never saved what it closed, and the mute has its own undo.
    const canReopen = task.undoable || !task.dismissed;
    const handleReopen = () => {
      if (task.undoable) undo.mutate({ taskId });
      else setStatus.mutate({ taskId, fromStatus, status: "open", resumeWork: false });
    };
    actions = (
      <>
        {task.threadId && <ThreadButton task={task} disabled={busy} onStart={() => {}} />}
        {canReopen && (
          <IconAction
            label="Reopen"
            statsigName="tasks-simple-reopen"
            disabled={busy || undo.isPending}
            onClick={handleReopen}
          >
            <Undo2 className="size-4" />
          </IconAction>
        )}
      </>
    );
  }

  return (
    <motion.li
      data-task-id={task.id}
      // Position only: the rows move up and down, and a full layout animation
      // would scale-project their widths.
      layout="position"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      // Out of the flow as it goes, so the rows below close up by sliding. A
      // task just done stays a beat first, long enough to see it checked.
      exit={doneNow ? DONE_EXIT : QUICK_EXIT}
      transition={{ duration: 0.18, ease: "easeOut" }}
      onAnimationEnd={flash.onAnimationEnd}
      // A brief brand tint as a task is done, before it leaves the list.
      style={doneNow ? { backgroundImage: DONE_TINT } : undefined}
      className={cn(
        compactRowClass,
        "group cursor-default gap-3 transition-[background-color] duration-300",
        flash.on && "animate-task-flash",
      )}
    >
      {leadingMark}
      {/* The due date sits right after the title rather than against the
          actions, so it reads as part of what the task says. The title gives
          way first when the row is short of room. */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Link
          href={userTaskHref(task.id)}
          className={cn(
            "min-w-0 truncate text-sm",
            // Weighted for the one state blocked on the user, as the thread list weights its own.
            !closed && taskThreadMark(task.threadStatus) === "needsAttention" && "font-semibold",
            "decoration-muted-foreground transition-colors",
            (closed || doneNow) && "text-muted-foreground",
            (task.status === "done" || doneNow) && "line-through",
          )}
          data-statsig-name="tasks-simple-open-detail"
        >
          {task.title}
        </Link>
        <RowNote task={task} due={due} back={back} />
      </div>
      {/* Shown on hover or keyboard focus, so a list at rest reads as titles.
          Faded rather than removed, so nothing shifts when they appear. A
          device without hover shows them always, and so does a thread that
          needs the user: Answer is the one thing the row is asking for. */}
      <div
        className={cn(
          "flex shrink-0 items-center gap-2 transition-opacity",
          !needsYou &&
            "opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100",
        )}
        data-row-actions
      >
        {actions}
      </div>
      {starting && <StartTaskDialog task={task} onClose={() => setStarting(false)} />}
    </motion.li>
  );
};

/** The one thing a row says besides its title: when it is due, when it comes back, or why it was dismissed. */
const RowNote = ({
  task,
  due,
  back,
}: {
  task: UserTask;
  due: ReturnType<typeof dueShort>;
  back: string | null;
}) => {
  const parts: ReactNode[] = [];
  if (due) {
    parts.push(
      <span key="due" className={cn(due.overdue && "font-semibold text-destructive")}>
        <span aria-hidden>Due {due.text}</span>
        <span className="sr-only">{due.spoken}</span>
      </span>,
    );
  }
  if (back) parts.push(<span key="back">{back}</span>);
  if (task.status === "cancelled") {
    const why = dismissedLabel(task);
    // A reason in the user's own words runs to 280 characters; capped, so the
    // title keeps its room, with the whole of it on hover.
    parts.push(
      <span key="why" className="max-w-48 truncate" title={why}>
        {why}
      </span>,
    );
  }
  if (parts.length === 0) return null;
  return (
    <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">{parts}</span>
  );
};

/**
 * "Dismissed", and why when the user said. The server stores the reason as
 * the words to show (the user's own, for Other); a bare reason code, which an
 * older write may have left, is put into words here.
 */
function dismissedLabel(task: UserTask): string {
  if (!task.closedReason) return "Dismissed";
  const code = dismissReasons.find((r) => r === task.closedReason);
  return `Dismissed · ${code ? DISMISS_REASON_LABEL[code] : task.closedReason}`;
}

/**
 * Start, or View once the task has a thread: it opens the thread. Start opens a composer with the
 * task attached; sending there opens a new thread linked to the task.
 */
/** How a row leaves when something other than Done moved it: out of the flow, quickly. */
const QUICK_EXIT = {
  opacity: 0,
  position: "absolute" as const,
  width: "100%",
  transition: { duration: 0.2 },
};

/**
 * How a row leaves once it is done, in three beats: it holds, tinted, while
 * the check and the burst land; it slides off to the right, keeping its place;
 * then its space closes and the rows below move up. The negative margin takes
 * the list's 12px gap with it, so the close-up ends without a jump.
 */
const DONE_EXIT = {
  x: 160,
  opacity: 0,
  height: 0,
  marginBottom: -12,
  transition: {
    x: { delay: 0.5, duration: 0.7, ease: "easeIn" as const },
    opacity: { delay: 0.6, duration: 0.6 },
    height: { delay: 1.2, duration: 0.3, ease: "easeInOut" as const },
    marginBottom: { delay: 1.2, duration: 0.3, ease: "easeInOut" as const },
  },
};

/** The brand gradient, faint, as a task is done. */
const DONE_TINT =
  "linear-gradient(139deg, color-mix(in srgb, var(--color-brand-500) 16%, var(--color-popover)), color-mix(in srgb, var(--color-purple-400) 22%, var(--color-popover)))";

/**
 * Whether the row glows: once, when the task gains a thread while the list is
 * on screen, which is Start's thread being linked as its first message lands.
 * Not on a task that already had one when the row appeared.
 */
function useThreadArrival(threadId: number | null | undefined) {
  const seen = useRef(threadId);
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (threadId && !seen.current) setOn(true);
    seen.current = threadId;
  }, [threadId]);
  return { on, onAnimationEnd: () => setOn(false) };
}

/**
 * Dismiss, as a glyph rather than a button: the quiet answer after the one
 * the row is for, and always last, so the ✕ lines up down the list. A ✕ and
 * not a bin: the task is not destroyed, it goes to Closed and can be undone.
 */
const DismissIcon = ({ disabled, onClick }: { disabled: boolean; onClick: () => void }) => (
  <IconAction
    label={DISMISS_LABEL}
    statsigName="tasks-simple-dismiss"
    disabled={disabled}
    onClick={onClick}
  >
    <X className="size-4" />
  </IconAction>
);

/** A row's quiet action, as a bare glyph in the last column: Dismiss, or Reopen. */
const IconAction = ({
  label,
  statsigName,
  disabled,
  onClick,
  children,
}: {
  label: string;
  statsigName: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <Button
    type="button"
    variant="ghost"
    size="action"
    aria-label={label}
    title={label}
    data-statsig-name={statsigName}
    disabled={disabled}
    onClick={onClick}
    className="shrink-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
  >
    {children}
  </Button>
);

/**
 * One width for a row's buttons (Keep, Start, View and Answer),
 * so they line up in columns down the list whichever a row shows.
 */
const ROW_ACTION_CLASS = "h-7 w-20 justify-center px-2 text-xs";

const ThreadButton = ({
  task,
  disabled,
  onStart,
}: {
  task: UserTask;
  disabled: boolean;
  onStart: () => void;
}) => {
  if (task.threadId) {
    // The one thread waiting on the user is the row's next step, so it is the
    // filled button and says what to do there; the rest are there to look in on.
    const needsYou = taskThreadMark(task.threadStatus) === "needsAttention";
    return (
      <Button
        asChild
        variant={needsYou ? "primary" : "outline"}
        size="sm"
        className={cn(ROW_ACTION_CLASS, needsYou ? "font-semibold" : "bg-white")}
        data-statsig-name="tasks-simple-view-thread"
      >
        <Link href={`/threads/${task.threadId}`}>
          <ThreadStatus task={task} onFilled={needsYou} />
          {needsYou ? "Answer" : "View"}
        </Link>
      </Button>
    );
  }
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn(ROW_ACTION_CLASS, "bg-white")}
      data-statsig-name="tasks-simple-start"
      disabled={disabled}
      onClick={onStart}
    >
      Start
    </Button>
  );
};

/** A task's done or reopen control: a circle, filled once the task is done. */
/**
 * The brand gradient's colours, brand blue-violet through purple, as the done
 * burst throws them. Hex, which is what the confetti library reads.
 */
const DONE_CONFETTI_COLORS = ["#6179f5", "#7e9bfb", "#5261eb", "#c084fc", "#d8b4fe"];

/**
 * A small confetti pop off the circle just ticked: a moment of done, aimed at
 * the row and gone in about a second, not a page-wide volley.
 *
 * On a canvas of its own, drawn on the main thread, as `CelebrationOverlay`
 * draws its volley: the library's default worker is blocked by our CSP and
 * paints nothing, and its default canvas is never sized.
 */
function celebrateDone(from: HTMLElement) {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const rect = from.getBoundingClientRect();
  const canvas = document.createElement("canvas");
  canvas.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:100";
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  document.body.appendChild(canvas);
  const burst = confetti.create(canvas, { resize: false, useWorker: false });
  const done = burst({
    particleCount: 36,
    spread: 75,
    startVelocity: 22,
    gravity: 0.9,
    ticks: 120,
    scalar: 0.75,
    angle: 75,
    origin: {
      x: (rect.left + rect.width / 2) / window.innerWidth,
      y: (rect.top + rect.height / 2) / window.innerHeight,
    },
    colors: DONE_CONFETTI_COLORS,
  });
  void (done ?? Promise.resolve()).finally(() => canvas.remove());
}

const CircleButton = ({
  checked = false,
  pop = false,
  label,
  statsigName,
  disabled,
  onClick,
}: {
  checked?: boolean;
  /** Whether the check pops in: only on the tap that ticks it. */
  pop?: boolean;
  label: string;
  statsigName: string;
  disabled: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => (
  <button
    type="button"
    aria-label={label}
    data-statsig-name={statsigName}
    disabled={disabled}
    onClick={onClick}
    className="relative flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
  >
    {checked ? (
      // Pops in on the tap that ticks it; a task already done draws it at rest.
      <motion.span
        initial={pop ? { scale: 0.6 } : false}
        animate={pop ? { scale: [0.6, 1.2, 1] } : undefined}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="flex text-primary"
      >
        <CircleCheck className="size-5" />
      </motion.span>
    ) : (
      <Circle className="size-5" />
    )}
  </button>
);

/**
 * Where the task's thread stands, ahead of View: the thread list's own
 * mark and colour, glyph only, so a row and its thread read as one thing. The
 * words are for a screen reader and the hover. Nothing when the server does
 * not say.
 */
const ThreadStatus = ({ task, onFilled }: { task: UserTask; onFilled: boolean }) => {
  const label = taskThreadStatusLabel(task);
  const mark = taskThreadMark(task.threadStatus);
  if (!label || !mark) return null;
  const { icon: Icon, className } = threadStatusConfig[mark];
  return (
    // On the filled button the mark takes the button's own text colour, or
    // purple on purple would draw nothing.
    <span
      className={cn("flex", !onFilled && className)}
      title={label}
      data-thread-status={task.threadStatus}
    >
      <Icon className="size-3.5" aria-hidden />
      <span className="sr-only">{label}, </span>
    </span>
  );
};
